import type { SelectItem } from '@mantine/core';
import { createStyles, Group, Skeleton } from '@mantine/core';
import { Text } from '@repo/foundations';
import { useCallback, useState } from 'react';
import { pluralize } from '../../utils/stringUtils';
import { Selector, SelectorTarget, SelectType } from '../Selectors';
import { SettingsBlock } from '../SettingsBlock';

interface SettingsMultiSelectProps {
	title: string;
	description?: string;
	values?: string[];
	options: SelectItem[];
	onChange: (values: string[]) => void;
	disabled?: boolean;
	searchable?: boolean;
	loading?: boolean;
	errorMessage?: string;
	itemName?: string;
	showSelectedLabel?: boolean;
}

const useStyles = createStyles((theme) => ({
	button: {
		'& .mantine-Button-inner': {
			paddingRight: theme.spacing['3xs'],
			paddingTop: theme.spacing['3xs'],
			paddingBottom: theme.spacing['3xs'],
			paddingLeft: theme.spacing.xs,
		},
		marginRight: -4,
		height: 'auto',
		padding: 0,
	},
}));

export function SettingsMultiSelect({
	title,
	description,
	values,
	options,
	onChange,
	errorMessage,
	disabled = false,
	searchable = false,
	loading = false,
	itemName = 'item',
	showSelectedLabel = false,
}: SettingsMultiSelectProps) {
	const { classes, theme } = useStyles();
	const [initialValues, setInitialValues] = useState<string[]>(values ?? []);

	const getItems = useCallback(
		(searchTerm: string) =>
			Promise.resolve(
				options
					.filter((item) => !searchTerm || item.label?.includes(searchTerm))
					.map((item) => ({
						label: item.label ?? '',
						value: item.value ?? '',
					}))
			),
		[options]
	);

	const handleChange = useCallback(
		(changedValues: string[]) => {
			setInitialValues(changedValues);
			onChange(changedValues);
		},
		[onChange]
	);

	return (
		<SettingsBlock
			title={title}
			description={description}
			rightComponent={
				loading ? (
					<Skeleton height={theme.other.space[7]} width={100} />
				) : (
					<Selector
						initialValue={initialValues}
						getItems={getItems}
						onChange={handleChange}
						selectType={SelectType.Multiple}
						disableSearch={!searchable}
						disabled={disabled}
					>
						<SelectorTarget variant="tertiary" className={classes.button}>
							<Group spacing="2xs">
								<Text size="sm">
									{showSelectedLabel && initialValues?.length === 1
										? (options.find((item) => item.value === initialValues?.[0])
												?.label ??
											pluralize(itemName, initialValues?.length, true))
										: pluralize(itemName, initialValues?.length, true)}
								</Text>
							</Group>
							{errorMessage && (
								<Text size="sm" variant="error">
									{errorMessage}
								</Text>
							)}
						</SelectorTarget>
					</Selector>
				)
			}
		/>
	);
}
